/** @jsx jsx */
import { Button } from '@datacamp/waffles/button';
import { Heading } from '@datacamp/waffles/heading';
import { mediaQuery } from '@datacamp/waffles/helpers';
import { useMediaQuery } from '@datacamp/waffles/hooks';
import {
  ArrowRight,
  FacebookBrand,
  LinkedInBrand,
  Share,
  TwitterBrand,
} from '@datacamp/waffles/icon';
import { Link } from '@datacamp/waffles/link';
import { Paragraph } from '@datacamp/waffles/paragraph';
import { Popover } from '@datacamp/waffles/popover';
import { darkThemeStyle, lightThemeStyle } from '@datacamp/waffles/theme';
import { tokens } from '@datacamp/waffles/tokens';
import { jsx } from '@emotion/react';
import { FloatingFocusManager, useFloating } from '@floating-ui/react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import config from '../../config';
import { isFeatureEnabled } from '../../helpers/featureFlags';
import { useSelector } from '../../interfaces/State';
import * as selectors from '../../redux/selectors';
import { selectStreakDays, selectStreakWeekView } from '../../redux/selectors';

import { DoubleFreezeIcon } from './FreezeIcon';
import { getSocialLinks } from './getSocialLinks';
import { isStreakMilestone } from './isStreakMilestone';
import { StreakWidget } from './StreakWidget';

export type DailyStreakScreenProps = {
  onExit: () => void;
};

function useCopy(
  streakDays?: number,
): { continueLabel: string; headerText: string; hintText: string } {
  const { t } = useTranslation();

  if (!streakDays) {
    return {
      headerText: '',
      hintText: '',
      continueLabel: '',
    };
  }

  if (streakDays === 1) {
    return {
      headerText: `${t('DailyStreakModal.headerStarted')} ⚡️`,
      hintText: t('DailyStreakModal.hintFirstDay'),
      continueLabel: t('DailyStreakModal.cta'),
    };
  }

  if (streakDays < 7) {
    return {
      headerText: `${t('DailyStreakModal.headerFirstWeek')} 🚀`,
      hintText: t('DailyStreakModal.hintFirstWeek', { count: 7 - streakDays }),
      continueLabel: t('DailyStreakModal.cta'),
    };
  }

  return {
    headerText: `${t('DailyStreakModal.headerExtended')} 🚀`,
    hintText: t('DailyStreakModal.hintLongTerm'),
    continueLabel: t('DailyStreakModal.cta'),
  };
}

const FreezesAwarded: React.FC = () => {
  const { t } = useTranslation();

  return (
    <>
      <DoubleFreezeIcon />
      <Heading
        css={{
          fontSize: '32px',
        }}
        data-testid="freezes-awarded-header"
      >
        {t('DailyStreakModal.freezeOwnedHeader')}
      </Heading>
      <Paragraph
        css={{
          marginBottom: tokens.spacing.xlarge,
          fontSize: tokens.fontSizes.medium,
        }}
      >
        {t('DailyStreakModal.freezeAwardedMessage')}
      </Paragraph>
    </>
  );
};

const StreakDayReached: React.FC = () => {
  const streakDays = useSelector(selectStreakDays);
  const streakWeekView = useSelector(selectStreakWeekView);
  const { isAboveSmall } = useMediaQuery();

  const { headerText, hintText } = useCopy(streakDays);

  if (streakDays === undefined) {
    return null;
  }

  return (
    <>
      <StreakWidget
        isMobile={!isAboveSmall}
        streakLengthInDays={streakDays}
        streakWeekView={streakWeekView}
      />
      <Heading
        data-testid="streak-day-reached-header"
        css={{
          marginTop: tokens.spacing.xlarge,
          fontSize: '32px',
        }}
      >
        {headerText}
      </Heading>
      <Paragraph
        css={{
          marginBottom: tokens.spacing.xlarge,
        }}
      >
        {hintText}
      </Paragraph>
    </>
  );
};

const DailyStreakScreen: React.FC<DailyStreakScreenProps> = (props) => {
  const { t } = useTranslation();
  const { onExit } = props;

  const { context } = useFloating();
  const autoFocusRef = useRef(null);
  const { isAboveSmall } = useMediaQuery();

  const streakDays = useSelector(selectStreakDays);
  const userId = useSelector(selectors.selectUserSettings).get('id');

  const [isShareOpen, setIsShareOpen] = useState(false);
  const [isFreezeModalOpen, setIsFreezeModalOpen] = useState(false);
  const { continueLabel } = useCopy(streakDays);

  const hasFreezeAttributionEnabled = useMemo(() => {
    if (streakDays === undefined) {
      return false;
    }

    const hasStreakLogicEnabled = isFeatureEnabled('wl--streak-freezes');

    if (!hasStreakLogicEnabled) {
      return false;
    }

    return isFeatureEnabled('wl--streak-freeze-attribution');
  }, [streakDays]);

  const isMilestone = isStreakMilestone(streakDays ?? 0);
  const { facebookUrl, linkedInUrl, twitterUrl } = getSocialLinks(
    streakDays ?? 0,
  );

  useEffect(() => {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'heap_event',
        heap_event_name: 'Learn - Campus - Streak Completed',
        heap_event_properties: {
          app_id: config.appName,
          event_category: 'learning',
          identity: userId,
          isMilestone,
          streakDays,
        },
      });
    }
  }, [isMilestone, streakDays, userId]);

  if (streakDays === undefined) {
    return null;
  }

  const shouldShowFreezeModal =
    hasFreezeAttributionEnabled && (streakDays === 2 || streakDays % 5 === 0);

  const onContinue = () => {
    if (!shouldShowFreezeModal || isFreezeModalOpen) {
      onExit();
    } else {
      setIsFreezeModalOpen(true);
    }
  };

  return (
    <FloatingFocusManager
      context={context}
      initialFocus={autoFocusRef}
      modal
      closeOnFocusOut={false}
    >
      <div
        css={[
          darkThemeStyle,
          {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: tokens.colors.navy,
            zIndex: tokens.zIndex.modal,
            display: 'flex',
            flexDirection: 'column',
            padding: tokens.spacing.xlarge,
            justifyContent: 'center',
            alignItems: 'stretch',
          },
        ]}
      >
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexGrow: 1,
            [mediaQuery.aboveSmall]: { flexGrow: 0 },
            flexDirection: 'column',
            textAlign: 'center',
          }}
        >
          {isFreezeModalOpen ? <FreezesAwarded /> : <StreakDayReached />}
        </div>
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            flexWrap: 'wrap',
            gap: tokens.spacing.medium,
            position: 'relative',
          }}
        >
          {isMilestone && (
            <Popover
              css={[lightThemeStyle, { minHeight: 'unset' }]}
              closeOnOutsidePress
              isOpen={isShareOpen}
              onClose={() => setIsShareOpen(false)}
              placement="topLeft"
              target={
                <Button
                  variant="cta"
                  iconLeft={<Share />}
                  fullWidth={!isAboveSmall}
                  onClick={() => setIsShareOpen(!isShareOpen)}
                  data-trackid={'campus-learn-streak-share-achievement'}
                >
                  {t('DailyStreakModal.share')}
                </Button>
              }
            >
              <Popover.Header>
                {t('DailyStreakModal.sharePopover.header')}
              </Popover.Header>
              <div
                css={{
                  a: {
                    flexGrow: 1,
                    fontSmoothing: 'antialiased',
                    marginBottom: 0,
                    WebkitFontSmoothing: 'antialiased',
                    border: 'none',
                  },
                  display: 'flex',
                  gap: tokens.spacing.small,
                  justifyContent: 'center',
                  padding: `0 ${tokens.spacing.medium} ${tokens.spacing.medium}`,
                  flexWrap: 'wrap',
                }}
              >
                <Button
                  aria-label={t('ShareAccomplishment.shareOnLinkedIn')}
                  as={Link}
                  target="_blank"
                  css={{
                    ':hover:not(:disabled)': {
                      background: '#0e76a8',
                    },
                    background: '#0e76a8',
                    color: tokens.colors.white,
                  }}
                  data-trackid={
                    'campus-learn-streak-share-achievement-linkedin'
                  }
                  href={linkedInUrl}
                >
                  <LinkedInBrand color={tokens.colors.white} />
                </Button>
                <Button
                  aria-label={t('ShareAccomplishment.shareOnFacebook')}
                  as={Link}
                  target="_blank"
                  css={{
                    ':hover:not(:disabled)': {
                      background: tokens.colors.brandFacebook,
                    },
                    background: tokens.colors.brandFacebook,
                    color: tokens.colors.white,
                  }}
                  data-trackid={
                    'campus-learn-streak-share-achievement-facebook'
                  }
                  href={facebookUrl}
                >
                  <FacebookBrand color={tokens.colors.white} />
                </Button>
                <Button
                  aria-label={t('ShareAccomplishment.shareOnTwitter')}
                  as={Link}
                  target="_blank"
                  css={{
                    ':hover:not(:disabled)': {
                      background: tokens.colors.brandTwitter,
                    },
                    background: tokens.colors.brandTwitter,
                    color: tokens.colors.white,
                  }}
                  data-trackid={'campus-learn-streak-share-achievement-twitter'}
                  href={twitterUrl}
                >
                  <TwitterBrand color={tokens.colors.white} />
                </Button>
              </div>
            </Popover>
          )}
          <Button
            ref={autoFocusRef}
            variant={isMilestone ? 'regularOutline' : 'cta'}
            iconRight={isAboveSmall ? <ArrowRight /> : undefined}
            fullWidth={!isAboveSmall}
            onClick={onContinue}
          >
            {continueLabel}
          </Button>
        </div>
      </div>
    </FloatingFocusManager>
  );
};

export default DailyStreakScreen;
